import { useEffect, useRef, useState } from 'react';
import { Send, Loader2, MessageSquare, StickyNote, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { commsAPI, commsFilesAPI, errMsg, notifyCommsChanged } from '../../lib/communicationsAPI';
import { RichTextEditor } from './RichTextEditor';
import { AttachmentPicker } from './Attachments';
import { EmailComposer } from './EmailComposer';

const isNoteMode = (m) => m === 'note';

const MentionPicker = ({ staff, onPick }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-amber-800 dark:text-amber-200" data-testid="comms-mention-btn"><AtSign className="w-4 h-4 mr-1" />Mention</Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1" data-testid="comms-mention-picker">
        {staff.map((u) => (
          <button key={u.id} type="button" onClick={() => { onPick(u); setOpen(false); }} className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-muted" data-testid={`comms-mention-option-${u.id}`}>
            {u.name || u.email}<span className="block text-[11px] text-muted-foreground">{u.email}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

// One TipTap composer with a "Reply to client" | "Internal note" toggle, attachments and draft autosave.
export const Composer = ({ convId, staff, onSent, disabled, conv, client, mailboxes = [] }) => {
  const [mode, setMode] = useState('reply');
  const [channel, setChannel] = useState(conv?.channel === 'email' ? 'email' : 'portal');
  const canPickChannel = conv?.channel !== 'email' && !!client?.email && mailboxes.length > 0;
  const emailMode = !isNoteMode(mode) && channel === 'email';
  const [html, setHtml] = useState('');
  const [mentions, setMentions] = useState([]);
  const [atts, setAtts] = useState([]);
  const [sending, setSending] = useState(false);
  const [draftSaved, setDraftSaved] = useState(null);
  const editorRef = useRef(null);
  const saveTimer = useRef(null);
  const isNote = mode === 'note';
  const baseline = useRef('');
  const blank = (v) => !v || !v.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

  useEffect(() => {
    let alive = true;
    baseline.current = '';
    commsFilesAPI.getDraft(convId).then((r) => {
      const d = r.data.draft;
      if (alive && d?.channel === 'email') { setChannel('email'); return; }
      if (alive && d) { baseline.current = d.body; editorRef.current?.setHtml(d.body); setHtml(d.body); setDraftSaved(d.updated_at); }
    }).catch(() => {});
    return () => { alive = false; clearTimeout(saveTimer.current); };
  }, [convId]);

  const onHtml = (v) => {
    setHtml(v);
    clearTimeout(saveTimer.current);
    if (isNote) return; // drafts are for client replies
    // Only autosave real edits: never on open/load, and never an empty body unless it clears an existing draft.
    if (v === baseline.current || (blank(v) && blank(baseline.current))) return;
    saveTimer.current = setTimeout(() => commsFilesAPI.saveDraft(convId, v).then((r) => { baseline.current = v; setDraftSaved(r.data.draft?.updated_at || null); }).catch(() => {}), 2000);
  };
  const switchMode = (m) => {
    clearTimeout(saveTimer.current);
    if (mode === 'reply' && html && html !== baseline.current && !blank(html)) commsFilesAPI.saveDraft(convId, html).catch(() => {});
    editorRef.current?.clear(); setHtml(''); setMentions([]); setMode(m);
    if (m === 'reply') commsFilesAPI.getDraft(convId).then((r) => { if (r.data.draft && r.data.draft.channel !== 'email') { editorRef.current?.setHtml(r.data.draft.body); setHtml(r.data.draft.body); } }).catch(() => {});
  };
  const pickMention = (u) => {
    editorRef.current?.insertText(`@${(u.name || u.email).replace(/\s+/g, '_')} `);
    setMentions((ms) => Array.from(new Set([...ms, u.id])));
  };

  const canSend = html.trim() || (!isNote && atts.length > 0);
  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      if (isNote) await commsAPI.addNote(convId, { body: editorRef.current?.getText() || '', mentions });
      else {
        clearTimeout(saveTimer.current);
        await commsAPI.reply(convId, html, { body_format: 'html', attachment_ids: atts.map((a) => a.id) });
        setAtts([]); setDraftSaved(null);
      }
      editorRef.current?.clear(); setHtml(''); setMentions([]);
      toast.success(isNote ? 'Internal note added' : 'Reply sent via Client Portal');
      notifyCommsChanged(); onSent();
    } catch (err) { toast.error(errMsg(err, 'Failed to send')); } finally { setSending(false); }
  };

  return (
    <div className={cn('border-t border-border p-3 space-y-2', isNote && 'bg-amber-50/70 dark:bg-amber-950/20')} data-testid="comms-composer">
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-md border border-border p-0.5" role="tablist" aria-label="Composer mode">
          {[['reply', 'Reply to client', MessageSquare], ['note', 'Internal note', StickyNote]].map(([k, label, Icon]) => (
            <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => mode !== k && switchMode(k)} data-testid={`comms-composer-tab-${k}`}
              className={cn('flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors',
                mode === k ? (k === 'note' ? 'bg-amber-200/80 text-amber-900 dark:bg-amber-800/40 dark:text-amber-100' : 'bg-primary text-primary-foreground') : 'text-muted-foreground hover:bg-muted/60')}>
              <Icon className="w-3.5 h-3.5" />{label}
            </button>
          ))}
        </div>
        {canPickChannel && !isNote && (
          <div className="inline-flex rounded-md border border-border p-0.5" role="tablist" aria-label="Channel">
            {[['portal', 'Portal'], ['email', 'Email']].map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={channel === k} onClick={() => setChannel(k)} data-testid={`comms-channel-${k}`}
                className={cn('px-2.5 py-1 rounded text-xs font-medium', channel === k ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted/60')}>{l}</button>
            ))}
          </div>
        )}
        {!emailMode && <span className={cn('ml-auto text-[11px] font-medium px-2 py-0.5 rounded-full', isNote ? 'bg-amber-200/60 text-amber-900 dark:text-amber-100' : 'bg-primary/10 text-primary')} data-testid="comms-channel-indicator">
          {isNote ? 'Visible to firm staff only' : 'Sending via Client Portal'}
        </span>}
      </div>
      {emailMode ? <EmailComposer key={`${convId}-email`} convId={convId} conv={conv} client={client} mailboxes={mailboxes} onSent={() => onSent()} /> : <>
      <RichTextEditor ref={editorRef} onChange={onHtml} onSubmit={send} disabled={disabled}
        placeholder={isNote ? 'Add an internal note for your team…' : 'Write a reply to the client…'} testId={isNote ? 'comms-note-input' : 'comms-reply-input'} />
      <div className="flex items-center gap-2">
        {isNote ? <MentionPicker staff={staff} onPick={pickMention} /> : <AttachmentPicker value={atts} onChange={setAtts} conversationId={convId} disabled={disabled} />}
        <span className="text-[11px] text-muted-foreground" data-testid="comms-draft-status">Ctrl/⌘ + Enter to send{!isNote && draftSaved ? ' · Draft saved' : ''}</span>
        <Button size="sm" onClick={send} disabled={sending || !canSend || disabled} className={cn('ml-auto', isNote && 'bg-amber-500 hover:bg-amber-600 text-white')}
          data-testid={isNote ? 'comms-note-submit' : 'comms-reply-send'}>
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}{isNote ? 'Add note' : 'Send reply'}
        </Button>
      </div>
      </>}
    </div>
  );
};
